import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CellClickedEvent,
  ColDef,
  GetDataPath,
  GridApi,
  SideBarDef,
} from "ag-grid-community";

import { useNavigate } from "react-router";
import { AgGridReact } from "ag-grid-react";
import { Box } from "@mui/material";
import _ from "lodash";
import {
  getCurrencySymbol,
  getCurrencyType,
} from "../../../../utils/currencyFormatter";
import {
  tableCellStyle,
  tableEntryStyle,
} from "../../../../components/TableComponent";
import { useGetCompanyName, HStack } from "../../../../components/utils";
import { useAuthStore } from "../../../../store";

import { agConfigDashboardSideBar } from "../../../dashboardPage/AgGridConfig";
import {
  RoundsToolTip,
  financeCustomRatiosToolTip,
  financeCustomToolTip,
} from "../../../dashboardPage/customTooltip";

import { TimeLineRenderer } from "./MisAGComponent";
import { keysList } from "../../../../constants/financeConstants";
import { handleEventForTracking } from "../../../../amplitudeAnalytics";

function AGGridMisTable({
  data,
  timelineType,
}: {
  data: any;
  timelineType: string;
}) {
  const companyName = useGetCompanyName();
  const companyId = useAuthStore().companyId ?? "";
  const userId = useAuthStore().user?.userId ?? 0;
  const gridApi = useRef<any>(null);
  const gridRef = useRef<AgGridReact>(null);
  const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);
  const currencySymbol = getCurrencySymbol();

  const uniqueKeys = Array.from(
    new Set(
      data
        ?.flatMap((entry: any) => Object.keys(entry.data))
        .filter((key: any) => !keysList.includes(key))
    )
  );

  const valuesByYear: { [year: string]: { [key: string]: number } } = {};

  data?.forEach((entry: any) => {
    const year = entry.year;
    valuesByYear[year] = {};
    uniqueKeys.forEach((key: any) => {
      valuesByYear[year][key] = 0;
    });
  });

  data?.forEach((entry: any) => {
    const year = entry.year;
    const dataValues = entry.data;
    uniqueKeys.forEach((key: any) => {
      if (key in dataValues) {
        valuesByYear[year][key] = Number(dataValues[key]);
      }
    });
  });

  const newData = data?.map((entry: any) => ({
    companyId: entry.companyId,
    cin: entry.cin,
    name: entry.name,
    timeline: entry.timeline,
    timelineType: entry.timelineType,
    year: entry.year,
    data: valuesByYear[entry.year],
    isPrivate: entry.isPrivate,
    publicCompanyName: entry.publicCompanyName,
  }));

  if (timelineType === "Year") {
    newData?.sort(
      (a: any, b: any) => parseInt(b.year, 10) - parseInt(a.year, 10)
    );
  } else if (timelineType === "Quarterly") {
    newData?.sort((a: any, b: any) => {
      if (a.year !== b.year) {
        return b.year - a.year;
      } else {
        const quartersOrder = ["Q1", "Q2", "Q3", "Q4"];
        return (
          quartersOrder.indexOf(b.timeline) - quartersOrder.indexOf(a.timeline)
        );
      }
    });
  } else if (timelineType === "Monthly") {
    newData?.sort((a: any, b: any) => {
      if (a.year === b.year) {
        const months = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        return months.indexOf(b.timeline) - months.indexOf(a.timeline);
      } else {
        return b.year - a.year;
      }
    });
  }

  useEffect(() => {
    if (data && timelineType === "Year") {
      const newColumnDefs = data.map((template: any) => ({
        headerName: `${template.timeline}`,
        field: template.timeline,
        filter: "agNumberColumnFilter",
        cellRenderer: "timeline",
        initialWidth: 300,
        sortable: true,
        autoHeight: true,
        cellStyle: tableCellStyle,
        wrapText: true,
        suppressSizeToFit: true,
        menuTabs: ["filterMenuTab"],
        tooltipField: "tooltipText",
        tooltipComponent: financeCustomRatiosToolTip,
        // Specify the field containing tooltip text
      }));
      setColumnDefs(newColumnDefs);
    } else if (data && timelineType === "Monthly") {
      const newColumnDefs = data.map((template: any) => ({
        headerName: `${template.year}-${template.timeline} (${currencySymbol})`,
        field: template.timeline,
        filter: "agNumberColumnFilter",
        cellRenderer: "timeline",
        initialWidth: 300,
        sortable: true,
        autoHeight: true,
        cellStyle: tableCellStyle,
        wrapText: true,
        suppressSizeToFit: true,
        menuTabs: ["filterMenuTab"],
        tooltipField: "tooltipText",
        tooltipComponent: financeCustomToolTip,
      }));
      setColumnDefs(newColumnDefs);
    } else if (data && timelineType === "Quarterly") {
      const newColumnDefs = data.map((template: any) => ({
        headerName: `${template.year}-${template.timeline} (${currencySymbol})`,
        field: template.timeline,
        filter: "agNumberColumnFilter",
        cellRenderer: "timeline",
        initialWidth: 300,
        sortable: true,
        autoHeight: true,
        cellStyle: tableCellStyle,
        wrapText: true,
        suppressSizeToFit: true,
        menuTabs: ["filterMenuTab"],
        tooltipField: "tooltipText",
        tooltipComponent: financeCustomToolTip,
      }));
      setColumnDefs(newColumnDefs);
    } else {
      setColumnDefs([]);
    }
  }, [data]);

  const componentsRegistery = useMemo(
    () => ({
      timeline: memo(TimeLineRenderer),
    }),
    []
  );

  const navigate = useNavigate();

  const onClickShareholderName = (id: string) => {
    handleEventForTracking({ eventName: `Shareholder Name` });
    navigate(`/shareholders/individual/${id}`);
  };

  const isValidDate = (date: string) =>
    Number.isNaN(date) &&
    new Date(date).toString() !== "Invalid Date" &&
    !Number.isNaN(Date.parse(date));

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      autoHeight: true,
      wrapHeaderText: true,
      suppressColumnVirtualisation: true,
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      filterParams: {
        buttons: ["reset"],
        maxNumConditions: 5,
      },
      minWidth: 150,
      filter: true,
      resizable: true,
      flex: 1,
    }),
    [data]
  );

  const autoGroupColumnDef = useMemo<ColDef>(
    () => ({
      headerName: "Group Name",
      minWidth: 300,
      menuTabs: [],
      cellRenderer: "agGroupCellRenderer",
      cellRendererParams: {
        suppressCount: true,
      },
      pinned: true,
    }),
    [data]
  );

  const getDataPath = useMemo<GetDataPath>(
    () => (data: any) => data.orgHierarchy,
    []
  );

  const keyMappings: any = {};

  uniqueKeys.forEach((key: any) => {
    const normalWord = key
      .split(/(?=[A-Z])/)
      .join(" ")
      .toLowerCase()
      .replace(/(?:^|\s)\S/g, (a: any) => a.toUpperCase());
    keyMappings[key] = normalWord;
  });

  const rowData = useMemo(() => {
    if (!data) return [];
    const rows: any = [];
    Object.keys(keyMappings).forEach((key) => {
      const normalWord = keyMappings[key];
      const timelineData = data.map((template: any) => {
        const timeline = template.timeline;
        const value = Number(template.data[key]) || 0;
        return {
          orgHierarchy: [normalWord],
          [timeline]: value,
        };
      });
      rows.push(...timelineData);
    });
    return rows;
  }, [data, keyMappings]);

  const groupedData = rowData.reduce((groups: any, item: any) => {
    const key = JSON.stringify(item.orgHierarchy);
    (groups[key] = groups[key] || []).push(item);
    return groups;
  }, {});

  const groupedArray = Object.values(groupedData);
  const combinedData = groupedArray.flatMap((group: any) => {
    const combinedGroup = Object.assign({}, ...group);

    return [combinedGroup];
  });

  const handleCellClick = (cellParams: CellClickedEvent<any, any>) => {
    if (cellParams.column.getColId() !== "action-column") {
      const template = cellParams.data;
      if (cellParams.column.getColId() === "shareholderName") {
        onClickShareholderName(template.id);
      }
    }
  };
  const [isColumnOpen, setIsColumnOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const openToolPanel = (key: any) => {
    if (key === "columns") {
      if (gridApi) {
        if (!isColumnOpen) gridApi?.current?.api?.openToolPanel(key);
        else gridApi?.current?.api?.closeToolPanel();
        setIsColumnOpen((state) => !state);
        setIsFilterOpen(false);
      }
    } else if (key === "filters") {
      if (gridApi) {
        if (!isFilterOpen) gridApi?.current?.api?.openToolPanel(key);
        else gridApi?.current?.api?.closeToolPanel();
        setIsFilterOpen((state) => !state);
        setIsColumnOpen(false);
      }
    }
  };

  const [filteredRowData, setFilteredRowData] = useState<any>([]);
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  const onAgGridFilterChanged = (grid: any) => {
    const filtersApplied = grid.api.isAnyFilterPresent();
    setIsFilterApplied(filtersApplied);

    const filteredData = grid.api
      .getModel()
      .rowsToDisplay.map((node: any) => node.data);
    setFilteredRowData(filteredData);
  };
  return (
    <div>
      <HStack className="justify-between w-full">
        <Box
          style={{
            height: 10 * 60,
          }}
          className="w-full max-h-full overflow-x-auto ag-theme-material h-full "
        >
          <AgGridReact
            sideBar={agConfigDashboardSideBar}
            ref={gridRef}
            onGridReady={(params) => {
              // Store the grid API referen
              gridApi.current = params;
            }}
            components={componentsRegistery}
            alwaysMultiSort
            animateRows={true}
            defaultColDef={defaultColDef}
            onFilterChanged={onAgGridFilterChanged}
            rowData={combinedData}
            onCellClicked={handleCellClick}
            columnDefs={columnDefs}
            autoGroupColumnDef={autoGroupColumnDef}
            treeData={true}
            groupDefaultExpanded={-1}
            pagination={false}
            getDataPath={getDataPath}
            suppressRowTransform={true}
            suppressCopyRowsToClipboard={true}
            suppressCopySingleCellRanges={true}
            suppressCellFocus={true}
            suppressMenuHide={false}
            tooltipShowDelay={1000}
            tooltipInteraction={true}
            rowClass={"border-t border-dashed "}
            overlayNoRowsTemplate={
              '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow; margin-top: 50px;">No Rows To Show</span>'
            }
          ></AgGridReact>
        </Box>
      </HStack>
    </div>
  );
}

export default AGGridMisTable;
